import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarClock, MessagesSquare } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { Reveal, Kicker } from "../components/Reveal";
import { CALENDLY_URL, COMPLIANCE_FAQ_SECTIONS } from "../data/content";
import { trackEvent } from "../lib/ga";

const CONSULT_LINK = "/contact?topic=Compliance%20Consulting";

const buildFaqSchema = () => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: COMPLIANCE_FAQ_SECTIONS.flatMap((section) =>
    section.items.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    }))
  ),
});

const ComplianceFaq = () => {
  let questionNumber = 0;
  return (
    <div data-testid="compliance-faq-page">
      <script type="application/ld+json">{JSON.stringify(buildFaqSchema())}</script>

      <PageHero
        kicker="Compliance Consulting FAQs"
        lines={[
          <span key="a">Compliance consulting —</span>,
          <span key="b">
            your questions <em className="italic text-[#3E9BDC]">answered.</em>
          </span>,
        ]}
      />

      <section className="py-16 md:py-24" data-testid="compliance-faq-intro">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <Reveal>
            <p className="text-base md:text-lg text-[#475569] leading-relaxed">
              Choosing compliance support starts with understanding what your firm needs and who
              will do the work. These answers explain common engagement choices for registered
              investment advisers and broker-dealers, including CCO support, examination preparation
              and ongoing assistance. American Elm Advisors is a financial services consulting firm
              based in Portsmouth, New Hampshire. For an overview of our capabilities, explore our{" "}
              <Link
                to="/compliance-consulting"
                data-testid="faq-intro-compliance-link"
                className="text-[#3E9BDC] font-semibold hover:underline underline-offset-4"
              >
                compliance consulting services
              </Link>{" "}
              or{" "}
              <Link
                to="/who-we-are"
                data-testid="faq-intro-team-link"
                className="text-[#3E9BDC] font-semibold hover:underline underline-offset-4"
              >
                meet our team
              </Link>
              .
            </p>
          </Reveal>

          <Reveal delay={0.08}>
            <div className="mt-8">
              <Link to={CONSULT_LINK} data-testid="faq-intro-cta" className="btn-primary">
                Discuss your compliance needs <ArrowRight size={16} />
              </Link>
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <nav
              className="mt-12 flex flex-wrap gap-3 border-t border-[#0A2B3D]/10 pt-8"
              aria-label="Jump to a section"
              data-testid="faq-section-nav"
            >
              {COMPLIANCE_FAQ_SECTIONS.map((section) => (
                <a
                  key={section.id}
                  href={`#${section.id}`}
                  data-testid={`faq-nav-${section.id}`}
                  className="text-xs font-medium tracking-[0.06em] uppercase text-[#0A2B3D] bg-[#EBF3F8] border border-[#A9CFE2]/60 px-4 py-2.5 hover:border-[#3E9BDC] hover:text-[#3E9BDC] transition-colors duration-300"
                >
                  {section.title}
                </a>
              ))}
            </nav>
          </Reveal>
        </div>
      </section>

      {COMPLIANCE_FAQ_SECTIONS.map((section) => (
        <React.Fragment key={section.id}>
          <section
            id={section.id}
            className="pb-16 md:pb-20 scroll-mt-28 border-t border-[#0A2B3D]/10 pt-16 md:pt-20"
            data-testid={`faq-section-${section.id}`}
          >
            <div className="max-w-3xl mx-auto px-5 sm:px-8">
              <Reveal>
                <Kicker>{section.title}</Kicker>
              </Reveal>
              <div className="mt-10 space-y-10">
                {section.items.map((item) => {
                  questionNumber += 1;
                  const n = questionNumber;
                  return (
                    <Reveal key={item.q} delay={0.04}>
                      <div data-testid={`faq-item-${n}`}>
                        <h2 className="font-serif font-bold text-xl md:text-2xl text-[#0A2B3D] leading-snug tracking-tight">
                          {item.q}
                        </h2>
                        <p className="mt-4 text-[#475569] leading-relaxed" data-testid={`faq-answer-${n}`}>
                          {item.a}
                        </p>
                        {item.link && (
                          <Link
                            to={item.link.to}
                            data-testid={`faq-answer-link-${n}`}
                            className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#3E9BDC] hover:underline underline-offset-4"
                          >
                            {item.link.label} <ArrowRight size={15} />
                          </Link>
                        )}
                      </div>
                    </Reveal>
                  );
                })}
              </div>
            </div>
          </section>

          {section.id === "scope-and-deliverables" && (
            <section className="pb-16 md:pb-20" data-testid="faq-inline-cta">
              <div className="max-w-3xl mx-auto px-5 sm:px-8">
                <Reveal>
                  <div className="bg-[#EBF3F8] border border-[#A9CFE2]/60 p-8 md:p-10 flex flex-col sm:flex-row sm:items-center gap-6">
                    <div className="flex items-start gap-4 flex-1">
                      <MessagesSquare size={24} className="text-[#3E9BDC] mt-1 shrink-0" />
                      <div>
                        <p className="font-serif font-bold text-lg md:text-xl text-[#0A2B3D] leading-snug">
                          Was this helpful?
                        </p>
                        <p className="mt-2 text-sm text-[#475569] leading-relaxed">
                          Every firm's situation is different. Tell us about yours and we'll help you
                          scope the right support.
                        </p>
                      </div>
                    </div>
                    <Link
                      to={CONSULT_LINK}
                      data-testid="faq-inline-cta-button"
                      onClick={() =>
                        trackEvent("faq_cta_click", {
                          cta: "faq_inline_discuss_situation",
                          source_page: "/compliance-faq",
                        })
                      }
                      className="btn-primary shrink-0 w-fit"
                    >
                      Discuss your situation <ArrowRight size={16} />
                    </Link>
                  </div>
                </Reveal>
              </div>
            </section>
          )}
        </React.Fragment>
      ))}

      <section className="bg-[#0A2B3D] text-white py-20 md:py-28" data-testid="faq-closing">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <Reveal>
            <Kicker light>Get Started</Kicker>
            <h2 className="mt-5 font-serif font-bold text-3xl sm:text-4xl tracking-tight leading-[1.15]">
              Discuss your compliance needs.
            </h2>
            <p className="mt-5 text-white/75 leading-relaxed max-w-2xl">
              Tell us about your firm, current priorities and the support you are looking for.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row sm:items-center gap-4">
              <Link to={CONSULT_LINK} data-testid="faq-closing-cta" className="btn-gold">
                Request a consultation <ArrowRight size={16} />
              </Link>
              <Link
                to="/who-we-are"
                data-testid="faq-closing-team-link"
                className="inline-flex items-center gap-2 text-sm font-semibold text-white/85 hover:text-[#3E9BDC] transition-colors"
              >
                Meet our team <ArrowRight size={15} />
              </Link>
            </div>
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="faq-closing-calendly"
              className="mt-6 inline-flex items-center gap-2 text-sm text-white/70 hover:text-[#3E9BDC] transition-colors"
            >
              <CalendarClock size={16} /> Or schedule a 15-minute call
            </a>

            <div className="mt-12 border-t border-white/10 pt-8" data-testid="faq-related-services">
              <p className="eyebrow text-[#A9CFE2] mb-4">Related Services</p>
              <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
                <Link to="/compliance-consulting" data-testid="faq-related-compliance" className="text-white/80 hover:text-white transition-colors">
                  Compliance consulting
                </Link>
                <span className="text-white/25">·</span>
                <Link to="/aml-independent-audits" data-testid="faq-related-aml" className="text-white/80 hover:text-white transition-colors">
                  AML independent audits
                </Link>
                <span className="text-white/25">·</span>
                <Link to="/trust-services-consulting" data-testid="faq-related-trust" className="text-white/80 hover:text-white transition-colors">
                  Trust company consulting
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default ComplianceFaq;
